import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/FeeModules.css";
import API_URL from "../apiConfig";

/**
 * Keep only the first occurrence of a student by id/email
 */
function uniqueBy(arr, keyFn) {
  const seen = new Set();
  return arr.filter(item => {
    const key = keyFn(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Parse a "session year" string like "2025-26" and return the numeric start year (2025).
 * Returns NaN if not parseable.
 */
function parseSessionStartYear(session) {
  if (!session) return NaN;
  // Accept formats: "2025-26", "2025", "2025/26", "2025 - 26"
  const m = String(session).match(/(\d{4})/);
  return m ? parseInt(m[1], 10) : NaN;
}

/**
 * Choose the most appropriate/latest batch entry for a student.
 * Accepts multiple shapes:
 * - student.session_year / student.batch (legacy fields)
 * - student.student_batches = [{ session_year, batch_id, batch_name, roll_no, course_year }, ...]
 * - student.batch_id on the root
 *
 * Preference:
 * 1) If student.student_batches is an array, pick entry with highest session start year, fallback to highest course_year.
 * 2) Else prefer session_year on root, then batch/batch_name/batch_id.
 */
function getLatestBatchInfo(student) {
  if (!student) return { display: "", batch_id: null, roll_no: "", session_year: null };

  // If student has joined student_batches array returned by some APIs, prefer that
  const sb = student.student_batches || student.batches || student.studentBatchList;
  if (Array.isArray(sb) && sb.length) {
    // choose by session_year start or by course_year if session not parseable
    let best = sb[0];
    let bestYear = parseSessionStartYear(sb[0]?.session_year) || Number(sb[0]?.course_year) || 0;
    for (let i = 1; i < sb.length; i++) {
      const cur = sb[i];
      const curYear = parseSessionStartYear(cur?.session_year) || Number(cur?.course_year) || 0;
      if (curYear > bestYear) {
        best = cur;
        bestYear = curYear;
      }
    }
    const display = best.session_year || best.session || best.batch_name || best.batch || best.batch_id || "";
    return { display, batch_id: best.batch_id ?? best.batch, roll_no: best.roll_no ?? student.roll_no ?? "", session_year: best.session_year ?? null };
  }

  // Not an array; check root-level fields
  const display = student.session_year || student.session || student.batch_name || student.batch || student.batch_id || "";
  return { display, batch_id: student.batch_id ?? student.batch ?? null, roll_no: student.roll_no ?? "", session_year: student.session_year ?? null };
}

export default function FeeListPage({ token }) {
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        // Add no-store to avoid cached responses in development or proxies
        const res = await fetch(`${API_URL}/api/students?_=${Date.now()}`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: "no-store",
        });
        if (!res.ok) throw new Error(`Failed to fetch students (${res.status})`);
        const data = await res.json();

        // Normalize array shape and compute a latestBatchDisplay for each student
        const arr = Array.isArray(data) ? data : data?.data ?? [];
        const normalized = arr.map(s => {
          const latest = getLatestBatchInfo(s);
          return {
            ...s,
            // standard fields used by this component
            _batch_display: latest.display || "",
            _batch_id: latest.batch_id ?? null,
            _roll_no: latest.roll_no ?? s.roll_no ?? "",
            _session_year: latest.session_year ?? (s.session_year ?? null),
          };
        });

        // Deduplicate by id/student_id/email and keep normalized values
        const uniq = uniqueBy(normalized, s => s.id ?? s.student_id ?? s.email ?? `${s.name}_${s._roll_no}`);
        if (!cancelled) setStudents(uniq);
      } catch (err) {
        console.error("Error fetching students:", err);
        if (!cancelled) setStudents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [token]);

  // Combined haystack search including normalized batch display/roll
  const filtered = students.filter(s => {
    const haystack = (
      (s.name || "") + " " +
      (s.email || "") + " " +
      (s._batch_display || "") + " " +
      (s.class || "") + " " +
      (s._session_year || s.session || s.session_year || "") + " " +
      (s._roll_no || s.roll_no || "")
    ).toLowerCase();
    return haystack.includes(search.trim().toLowerCase());
  });

  return (
    <div className="fee-list-container">
      <h2 className="fee-list-title">Fee List & Payment</h2>

      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
        <input
          className="fee-search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, email, roll no, batch, class, session"
          style={{ flex: 1 }}
        />
        <div style={{ minWidth: 140, textAlign: "right" }}>
          {loading ? <small>Loading...</small> : null}
        </div>
      </div>

      <table className="fee-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Batch</th>
            <th>Roll No</th>
            <th style={{ width: 140 }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 ? (
            <tr>
              <td colSpan={5} style={{ textAlign: "center" }}>{loading ? "Loading students..." : "No students found."}</td>
            </tr>
          ) : (
            filtered.map(s => {
              const key = s.id ?? s.student_id ?? s.email ?? `${s.name}_${s._roll_no}`;
              const batchDisplay = s._batch_display || s.session_year || s.batch || s.batch_id || "";
              const roll = s._roll_no || s.roll_no || "";
              return (
                <tr key={key}>
                  <td>{s.name ?? s.student_name ?? "-"}</td>
                  <td>{s.email ?? "-"}</td>
                  <td>{batchDisplay}</td>
                  <td>{roll}</td>
                  <td>
                    <button
                      className="fee-action-btn"
                      onClick={() => {
                        const sid = s.student_id || s.id || s._id;
                        if (!sid) {
                          alert("Student ID missing!");
                          return;
                        }
                        navigate(`/admin/manage-fees/list/${sid}`);
                      }}
                    >
                      View Account
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}